import React, {useMemo} from "react";
import {Stack, TableCell, Typography} from "@mui/material";
import {KeySnapshotUpdate, useKeyHistoryLastUpdatesStore} from "../../api/KeyHistoryLastUpdatesStore";
import {Page, StorePageCount} from "../../components/page/Page";
import {LoadingData} from "../../components/loading-data/LoadingData";
import {VirtualTable} from "../../components/data-table/VirtualTable";
import {TableColumnData} from "../../components/data-table/TableColumnData";
import {KeyChip} from "../../components/key-chip/KeyChip";
import {KeyLocationShield} from "../../components/location/KeyLocationShield";
import {useFilter} from "../../util/FilterSerializer";
import {filterKeyLastUpdatesRows, KeyLastUpdatesFilter, keyLastUpdatesFilterSerializer} from "./KeyLastUpdatesFilter";
import {KeyVault} from "../../api/Model";
import {KeyHistoryPeriod} from "../../components/key-history-period/KeyHistoryPeriod";
import {hashColor} from "../../components/color-picker/ColorPicker";
import {dateTimeString} from "../../util/DateUtil";
import {KeyHistoryButton} from "../history-dialog/HistoryDialogContext";
import {EmployeeHistoryButton} from "../history-dialog/HistoryDialogContext";
import {Principal} from "../../components/principal/Principal";


const columns: TableColumnData<KeySnapshotUpdate>[] = [
    {
        key: "key",
        header: "Key",
        width: "10em",
        cell: row => (
            <TableCell
                sx={{borderLeft: `4px solid ${row.historyRecord.updatedAt ? hashColor(row.historyRecord.updatedAt) : 'white'}`}}>
                <Stack direction="row" gap={1} alignItems="center" justifyContent="space-between">
                    <KeyChip keyData={row.key} record={row.historyRecord}/>
                    <KeyHistoryButton keyData={row.key}/>
                </Stack>
            </TableCell>
        ),
    },
    {
        key: "location",
        header: "Location",
        width: "20em",
        cell: row => (
            <TableCell>
                <Stack direction="row" gap={1} alignItems="center" justifyContent="space-between">
                    <KeyLocationShield location={row.historyRecord.location}/>
                    {row.historyRecord.location.type === 'with-employee' && (
                        <EmployeeHistoryButton employee={row.historyRecord.location.employee}/>
                    )}
                </Stack>
            </TableCell>
        ),
    },
    {
        key: "period",
        header: "Period",
        width: "14em",
        cell: row => (
            <TableCell>
                <KeyHistoryPeriod record={row.historyRecord}/>
            </TableCell>
        ),
    },
    {
        key: "updated-at",
        header: "Updated",
        width: "14em",
        cell: row => (
            <TableCell>
                <Typography variant="body2">
                    {dateTimeString(new Date(row.historyRecord.updatedAt))}
                </Typography>
                {row.historyRecord.updatedBy && (
                    <Typography variant="body2" component="span">
                        <Principal principalEmail={row.historyRecord.updatedBy}/>
                    </Typography>
                )}
            </TableCell>
        ),
    },
    {
        key: "notes",
        header: "Notes",
        cell: row => (
            <TableCell>
                {row.historyRecord.notes && (
                    <Typography variant="body2" color="text.secondary">
                        {row.historyRecord.notes}
                    </Typography>
                )}
            </TableCell>
        ),
    },
];

function rowId(row: KeySnapshotUpdate): string {
    return `${row.key.id}-${row.historyRecord.id}`;
}

function KeyLastUpdatesTable({rows}: {
    rows: KeySnapshotUpdate[],
}) {
    return (
        <VirtualTable
            columns={columns}
            rows={rows}
            id={rowId}
        />
    );
}

export function KeyLastUpdatesPage({keyVaults}: {
    keyVaults: KeyVault[],
}) {
    const rows = useKeyHistoryLastUpdatesStore(state => state.remoteValue);
    const filterSerializer = useMemo(
        () => keyLastUpdatesFilterSerializer(keyVaults),
        [keyVaults]
    );
    const [filter, setFilter] = useFilter(filterSerializer);

    const filteredRows = useMemo(
        () => rows.map(rows => filterKeyLastUpdatesRows(rows, filter)),
        [rows, filter],
    );

    return (
        <Page
            header="Last Updates"
            counter={<StorePageCount totalValue={rows} value={filteredRows}/>}
            filter={
                <KeyLastUpdatesFilter
                    filter={filter}
                    setFilter={setFilter}
                />
            }
        >
            <LoadingData
                totalValue={rows}
                value={filteredRows}
                emptyMessage="No updated key history records found."
                noMatchMessage="No key history updates match the current filters. Try adjusting your filter criteria."
                defaultErrorMessage="Failed to load key history updates. Please try again later."
            >
                {rows => <KeyLastUpdatesTable rows={rows}/>}
            </LoadingData>
        </Page>
    );
}
